<template>
  <div class="vehicle-info" v-if="vehicle">

    <div class="d-flex justify-end">
      <audit-trail-button entity-type="Vehicle" :entity-id="$route.params.id" />
    </div>


    <v-alert v-if="vehicle.archived" type="warning" text dense class="mb-4">
      {{ $t('vehicle.archived-since', { date: formatDate(vehicle.archivedAt, $t('format_datetime')) }) }}
    </v-alert>

    <div class="vehicle-fiche">
      <div class="right grid">

        <headline bg>
          {{ $tc('vehicle.vehicle') }}
          <template v-if="currentUser.permissions.includes('VEHICLE_WRITE')">
            <v-btn small class="ml-3" :to="{name: 'vehicle.edit'}">
              {{ $t('edit') }}
            </v-btn>
          </template>
        </headline>

        <div class="mx-3">
          <v-row>
            <v-col class="label" sm="3">{{ $t('vehicle.name') }} :</v-col>
            <v-col sm="3">{{ vehicle.name || '-' }}</v-col>
            <v-col class="label" sm="3">{{ $t('vehicle.registration-number') }} :</v-col>
            <v-col sm="3">{{ vehicle.registrationNumber || '-' }}</v-col>
          </v-row>

          <v-row>
            <v-col class="label" sm="3">{{ $t('vehicle.vin') }} :</v-col>
            <v-col sm="3">{{ vehicle.vin || '-' }}</v-col>
            <v-col class="label" sm="3">{{ $t('vehicle.country') }} :</v-col>
            <v-col sm="3">{{ vehicle.country || '-' }}</v-col>
          </v-row>
        </div>

        <template v-if="vehicle.version">
          <headline bg class="mt-10">
            {{ $t('vehicle.brand') }} / {{ $t('vehicle.model') }} / {{ $t('vehicle.version') }}
          </headline>

          <div class="mx-3">
            <v-row>
              <v-col class="label" sm="3">{{ $t('vehicle.brand') }} :</v-col>
              <v-col sm="3">{{ vehicleVersion.brand || '-' }}</v-col>
              <v-col class="label" sm="3">{{ $t('vehicle.model') }} :</v-col>
              <v-col sm="3">{{ vehicleVersion.model || '-' }}</v-col>
            </v-row>

            <v-row>
              <v-col class="label" sm="3">{{ $t('vehicle.version') }} :</v-col>
              <v-col sm="3">{{ vehicleVersion.version || '-' }}</v-col>
              <v-col class="label" sm="3">{{ $t('vehicle.type') }} :</v-col>
              <v-col sm="3">{{ vehicleVersion.type || '-' }}</v-col>
            </v-row>

            <v-row>
              <v-col class="label" sm="3">{{ $t('vehicle.car-body') }} :</v-col>
              <v-col sm="3">{{ vehicleVersion.carBody || '-' }}</v-col>
              <v-col class="label" sm="3">{{ $t('vehicle.connectors') }} :</v-col>
              <v-col sm="3">{{ displayConnectors(vehicleVersion.connectors) }}</v-col>
            </v-row>
          </div>
        </template>

        <headline bg class="mt-10">
          {{ $t('vehicle.specifications') }}
        </headline>

        <div class="mx-3">
          <v-row>
            <v-col class="label" sm="3">{{ $t('vehicle.mileage') }} :</v-col>
            <v-col sm="3">
              <template v-if="vehicle.lastMileageKm != null">
                {{ formatMileage(vehicle.lastMileageKm) }} km
              </template>
              <template v-else>-</template>
            </v-col>
            <v-col class="label" sm="3">{{ $t('vehicle.battery-level') }} :</v-col>
            <v-col sm="3">
              <template v-if="vehicle.lastBatteryLevelPercent != null">
                {{ vehicle.lastBatteryLevelPercent }} %
              </template>
              <template v-else>-</template>
            </v-col>
          </v-row>
        </div>

        <headline bg class="mt-10">
          {{ $t('vehicle.telematics-status') }}
        </headline>

        <div class="mx-3">
          <v-row>
            <v-col class="label" sm="3">{{ $t('vehicle.telematics') }} :</v-col>
            <v-col sm="3">
              <v-chip x-small :color="vehicle.telematicsConnection ? 'success' : 'grey'" text-color="white">
                {{ vehicle.telematicsConnection ? $t('vehicle.connected') : $t('vehicle.disconnected') }}
              </v-chip>
            </v-col>
            <v-col class="label" sm="3">{{ $t('vehicle.last-reading') }} :</v-col>
            <v-col sm="3">
              <template v-if="vehicle.lastTelemetryReadingAt">
                {{ formatDate(vehicle.lastTelemetryReadingAt, $t('format_datetime')) }}
              </template>
              <template v-else>-</template>
            </v-col>
          </v-row>
          <!-- Reimbursement (meter) mode: the home-charge meter this vehicle feeds — link to its page. -->
          <v-row v-if="meterDevice">
            <v-col class="label" sm="3">{{ $t('vehicle.meter-device') }} :</v-col>
            <v-col sm="9">
              <router-link :to="{ name: 'meter-device.detail', params: { id: meterDevice.id } }">
                {{ meterDevice.name }}
              </router-link>
            </v-col>
          </v-row>
        </div>

        <headline bg class="mt-10">
          {{ $t('vehicle.customer') }} / {{ $t('vehicle.organization') }}
        </headline>

        <div class="mx-3">
          <v-row>
            <v-col class="label" sm="3">{{ $tc('customer.customer') }} :</v-col>
            <v-col sm="3">
              <router-link v-if="vehicle.customer" :to="{name: 'customer.detail', params: {id: vehicle.customer.id}}">
                {{ vehicle.customer | customerLabel }}
              </router-link>
              <template v-else>-</template>
            </v-col>
            <v-col class="label" sm="3">{{ $t('organization.organization') }} :</v-col>
            <v-col sm="3">
              <router-link v-if="vehicle.organization" :to="{name: 'organizations.detail', params: {id: vehicle.organization.id}}">
                {{ vehicle.organization.name }}
              </router-link>
              <template v-else>-</template>
            </v-col>
          </v-row>
        </div>

        <headline bg class="mt-10">
          {{ $t('vehicle.created-at') }} / {{ $t('vehicle.updated-at') }}
        </headline>

        <div class="mx-3">
          <v-row>
            <v-col class="label" sm="3">{{ $t('vehicle.created-at') }} :</v-col>
            <v-col sm="3">{{ formatDate(vehicle.createdAt, $t('format_datetime')) }}</v-col>
            <v-col class="label" sm="3">{{ $t('vehicle.updated-at') }} :</v-col>
            <v-col sm="3">{{ vehicle.updatedAt ? formatDate(vehicle.updatedAt, $t('format_datetime')) : '-' }}</v-col>
          </v-row>
        </div>

      </div>
    </div>

    <!-- Les commandes d'équipement du véhicule (module d'onboarding) : véhicule connecté ou compteur, pour son membre. -->
    <vehicle-equipment-orders class="mt-10" :vehicle="vehicle" />

  </div>
</template>

<script>
import AuditTrailButton from "@elements/AuditTrailButton.vue";
import VehicleRepository from "@repository/VehicleRepository";
import VehicleEquipmentOrders from "@pages/Onboarding/Commissioning/VehicleEquipmentOrders.vue";

export default {
  components: {AuditTrailButton,  VehicleEquipmentOrders },

  data() {
    return {
      meterDevice: null,
    }
  },

  computed: {
    vehicle() {
      return this.$parent.vehicle
    },

    vehicleVersion() {
      return this.vehicle?.version || {}
    },
  },

  watch: {
    // The parent loads the vehicle asynchronously — fetch its connected-vehicle meter once the id is known.
    'vehicle.id': {
      immediate: true,
      handler(id) {
        if (!id) { this.meterDevice = null; return }
        VehicleRepository.getConnectedVehicleMeter(id)
            .then(res => { this.meterDevice = (res && res.meter) || null })
            .catch(() => { this.meterDevice = null })
      },
    },
  },

  methods: {
    formatMileage(value) {
      if (value == null) return '-'
      return value.toLocaleString()
    },

    displayConnectors(value) {
      if (!value) return '-'
      if (Array.isArray(value)) {
        return value.map(c => typeof c === 'object' ? (c.name || c.standard || c.type || '-') : c).join(', ') || '-'
      }
      if (typeof c === 'object') return value.name || value.standard || '-'
      return value
    },
  },
}
</script>

<style lang="scss">
  .vehicle-info {
    .vehicle-fiche {
      display: flex;
      .right {
        flex: 1;
      }
    }
  }
</style>
